<template>
	<Head :title="`${title} | ${appName}`"/>
	<section class="relative flex w-screen max-w-screen">
		<aside id="aareii-menu" class="fixed top-0 left-0 right-0 hidden h-dvh w-screen z-50 md:relative md:w-80 md:py-2 md:pl-2 md:flex md:z-auto overflow-y-auto">
			<nav class="p-2 w-full md:fixed md:w-76 md:h-[calc(100dvh-1rem)] flex flex-col bg-linear-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-950 shadow-md dark:shadow-black md:rounded-lg text-gray-800 dark:text-gray-100 divide-y divide-gray-200 dark:divide-gray-800 overflow-y-auto">
				<div class="px-2 pb-2 md:hidden flex items-center justify-between gap-3">
					<SecondaryButton :color="Color.Gray" @click.stop="toggleMenu">
						<i class="fa-solid fa-xmark"/>
					</SecondaryButton>
					<Link href="/">
						<img :src="storage('logos/SVG/white/letters.svg')" class="hidden dark:inline-block h-7.5" alt="AArEII Logo">
						<img :src="storage('logos/SVG/letters.svg')" class="dark:hidden h-7.5" alt="AArEII Logo">
					</Link>
				</div>
				<div id="menu-content" class="py-2 md:pt-0 grow flex flex-col items-stretch gap-1 overflow-y-auto">
					<template v-if="isLoggedIn">
						<template v-for="item in menuItems">
							<MenuItemComponent v-if="item.visible" :item="item"/>
						</template>
					</template>
				</div>
				<div id="menu-footer" class="pt-2 flex flex-col gap-1">
					<template v-if="isLoggedIn">
						<a v-if="language === 'en'" @click.stop="switchLanguage">Cambiar a español (AR)</a>
						<a v-else @click.stop="switchLanguage">Switch to English (US)</a>
						<Link :href="route('logout')" method="post" class="text-red-600 dark:text-red-400">{{ trans('Logout') }}</Link>
					</template>
					<Link v-else :href="route('login')" class="text-green-600 dark:text-green-400">{{ trans('Login') }}</Link>
					<div class="flex justify-between items-center gap-2">
						<Link :href="route('version.index')" class="text-pink-600 dark:text-pink-400">{{ appVersion }}</Link>
						<a v-if="canViewPulse" :href="route('pulse')" target="_blank" :class="`${currentRouteNamed('pulse') ? 'active' : ''} rounded-full size-6 flex items-center justify-center`">
							<i class="fa-solid fa-heart-pulse"/>
						</a>
						<ThemeChooser/>
					</div>
				</div>
			</nav>
		</aside>
		<main class="relative grow">
			<ActionableHeader
				:title="title"
				:subtitle="subtitle"
				:return-url="returnUrl"
				:return-label="returnLabel"
				@toggle-menu="toggleMenu"
			>
				<slot name="actions"/>
			</ActionableHeader>
			<section class="mx-auto w-full md:w-lg lg:w-3xl xl:w-5xl 2xl:w-6xl p-4 flex flex-col gap-3">
				<slot/>
			</section>
		</main>
	</section>
</template>
<script setup lang="ts">
import {route} from 'ziggy-js'
import {Color} from '@/Types/Enums/Color'
import {Link, Head} from '@inertiajs/vue3'
import {type Event} from '@/Types/Events/Event'
import {router, usePage} from '@inertiajs/vue3'
import ThemeChooser from '@/Components/UI/ThemeChooser.vue'
import {type Board} from '@/Types/Association/Boards/Board'
import {Rotation} from '@/Types/Periods/Rotations/Rotation'
import {type MenuItem} from '@/Types/Components/Menu/MenuItem'
import {type Campaign} from '@/Types/Tools/Campaigns/Campaign'
import MenuItemComponent from '@/Components/UI/Menu/MenuItem.vue'
import ActionableHeader from '@/Components/UI/ActionableHeader.vue'
import {type BoardArea} from '@/Types/Association/Boards/BoardArea'
import {type Assembly} from '@/Types/Association/Assemblies/Assembly'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {defineProps, computed, type ComputedRef, ref, type Ref} from 'vue'
import {getActiveLanguage, loadLanguageAsync, trans} from 'laravel-vue-i18n'
import {showInfo, showSuccess, showValidationErrors, storage} from '@/Helpers/general'

defineProps<{
	title:string,
	subtitle?:string,
	returnUrl?:string,
	returnLabel?:string,
}>()

const working:Ref<boolean> = ref(false)

const page = usePage()

const language:ComputedRef<string> = computed(getActiveLanguage)

function switchLanguage():void{
	if(!working.value){
		working.value = true
		showInfo(trans('system.language.switching'))
		const newLanguage:string = language.value === 'en' ? 'es' : 'en'
		router.get(route('locale.switch', newLanguage), {}, {
			onSuccess: () => {
				loadLanguageAsync(newLanguage).then(() => {
					showSuccess(trans('system.language.switched'))
				})
			},
			onError: showValidationErrors,
			onFinish: () => {
				working.value = false
			},
		})
	}
}

const appName:ComputedRef<string> = computed(():string => page.props.appName as string)
const appVersion:ComputedRef<string> = computed(():string => page.props.appVersion as string)
const isLoggedIn:ComputedRef<boolean> = computed(():boolean => page.props.isLoggedIn as boolean)
const currentRouteName:ComputedRef<string> = computed(():string => page.props.currentRouteName as string)
const currentEvent:ComputedRef<Event|null> = computed(():Event|null => page.props.currentEvent as Event|null)
const currentBoard:ComputedRef<Board> = computed(():Board => page.props.currentBoard as Board)
const currentCampaign:ComputedRef<Campaign|null> = computed(():Campaign|null => page.props.currentCampaign as Campaign|null)
const currentAssembly:ComputedRef<Assembly|null> = computed(():Assembly|null => page.props.currentAssembly as Assembly|null)
const currentRotation:ComputedRef<Rotation|null> = computed(():Rotation|null => page.props.currentRotation as Rotation|null)
const canViewPulse:ComputedRef<boolean> = computed(():boolean => page.props.canViewPulse as boolean)
const canViewAccreditations:ComputedRef<boolean> = computed(():boolean => page.props.canViewAccreditations as boolean)
const canEditJoSEII:ComputedRef<boolean> = computed(():boolean => page.props.canEditJoSEII as boolean)
const canEditCAEII:ComputedRef<boolean> = computed(():boolean => page.props.canEditCAEII as boolean)
const canEditSemEII:ComputedRef<boolean> = computed(():boolean => page.props.canEditSemEII as boolean)
const canEditLIDERA:ComputedRef<boolean> = computed(():boolean => page.props.canEditLIDERA as boolean)
const canEditCiCA:ComputedRef<boolean> = computed(():boolean => page.props.canEditCiCA as boolean)
const canEditENaR:ComputedRef<boolean> = computed(():boolean => page.props.canEditENaR as boolean)
const canEditRCD:ComputedRef<boolean> = computed(():boolean => page.props.canEditRCD as boolean)
const canEditGeneric:ComputedRef<boolean> = computed(():boolean => page.props.canEditGeneric as boolean)
const canViewStatistics:ComputedRef<boolean> = computed(():boolean => page.props.canViewStatistics as boolean)
const canViewRoles:ComputedRef<boolean> = computed(():boolean => page.props.canViewRoles as boolean)
const canViewBoards:ComputedRef<boolean> = computed(():boolean => page.props.canViewBoards as boolean)
const canViewBoardRepresentativeGroups:ComputedRef<boolean> = computed(():boolean => page.props.canViewBoardRepresentativeGroups as boolean)
const canViewUsers:ComputedRef<boolean> = computed(():boolean => page.props.canViewUsers as boolean)
const canViewAssemblies:ComputedRef<boolean> = computed(():boolean => page.props.canViewAssemblies as boolean)
const canViewEventPeriods:ComputedRef<boolean> = computed(():boolean => page.props.canViewEventPeriods as boolean)
const canViewEventRequests:ComputedRef<boolean> = computed(():boolean => page.props.canViewEventRequests as boolean)
const canViewRotationPeriods:ComputedRef<boolean> = computed(():boolean => page.props.canViewRotationPeriods as boolean)
const canViewRotationRequests:ComputedRef<boolean> = computed(():boolean => page.props.canViewRotationRequests as boolean)
const canViewRotations:ComputedRef<boolean> = computed(():boolean => page.props.canViewRotations as boolean)
const canViewAdmissionElections:ComputedRef<boolean> = computed(():boolean => page.props.canViewAdmissionElections as boolean)
const canViewAcademicCertificates:ComputedRef<boolean> = computed(():boolean => page.props.canViewAcademicCertificates as boolean)
const canViewCampaigns:ComputedRef<boolean> = computed(():boolean => page.props.canViewCampaigns as boolean)
const canViewSettings:ComputedRef<boolean> = computed(():boolean => page.props.canViewSettings as boolean)
const canViewCompanies:ComputedRef<boolean> = computed(():boolean => page.props.canViewCompanies as boolean)
const canViewColleges:ComputedRef<boolean> = computed(():boolean => page.props.canViewColleges as boolean)
const canViewMajors:ComputedRef<boolean> = computed(():boolean => page.props.canViewMajors as boolean)
const canViewLanguages:ComputedRef<boolean> = computed(():boolean => page.props.canViewLanguages as boolean)

const currentEventMenuItem:MenuItem|null = currentEvent.value ? {
	title: currentEvent.value.title,
	children: [
		{
			id: 'events.edit',
			title: trans('events.info.title'),
			link: route('events.edit', currentEvent.value.id),
		},
		{
			id: 'events.description',
			title: trans('events.description'),
			link: route('events.description', currentEvent.value.id),
		},
		{
			id: 'events.attractions',
			title: trans('events.attractions'),
			link: route('events.attractions', currentEvent.value.id),
		},
		{
			id: 'events.schedule',
			title: trans('events.schedule.title'),
			link: route('events.schedule', currentEvent.value.id),
		},
		{
			id: 'events.blueprints',
			title: trans('events.blueprints.title'),
			link: route('events.blueprints', currentEvent.value.id),
		},
		{
			id: 'events.organizers',
			title: trans('events.organizers.title'),
			link: route('events.organizers', currentEvent.value.id),
		},
		{
			id: 'events.sponsors',
			title: trans('events.sponsors.title'),
			link: route('events.sponsors', currentEvent.value.id),
		},
		{
			id: 'events.packs',
			title: trans('events.packs.title'),
			link: route('events.packs.index', currentEvent.value.id),
		},
		{
			id: 'events.bundles',
			title: trans('events.bundles.title'),
			link: route('events.bundles.index', currentEvent.value.id),
		},
		{
			title: trans('events.payments.title'),
			children: [
				{
					id: 'events.payments.providers.mercadopago',
					title: 'MercadoPago',
					link: route('events.payments.providers.mercadopago', currentEvent.value.id),
				},
				{
					id: 'events.payments.providers.paypal',
					title: 'PayPal',
					link: route('events.payments.providers.paypal', currentEvent.value.id),
				},
				{
					id: 'events.payments.providers.santander',
					title: 'Santander',
					link: route('events.payments.providers.santander', currentEvent.value.id),
				},
				{
					id: 'events.payments.payment-links.index',
					title: trans('events.payment_links.title'),
					link: route('events.payments.payment-links.index', currentEvent.value.id),
				},
				{
					id: 'events.payments.bank-accounts.index',
					title: trans('events.bank_accounts.title'),
					link: route('events.payments.bank-accounts.index', currentEvent.value.id),
				},
			],
		},
		{
			title: trans('events.instances.enrollments'),
			children: [
				{
					id: 'events.enrollments.index',
					title: trans('events.enrollments.title'),
					link: route('events.enrollments.index', currentEvent.value.id),
				},
				{
					id: 'events.enrollments.resolve',
					title: trans('events.enrollments.resolve'),
					link: route('events.enrollments.resolve', currentEvent.value.id),
				},
				{
					id: 'events.enrollments.purchase-orders.index',
					title: trans('enrollments.purchase_orders.title'),
					link: route('events.enrollments.purchase-orders.index', currentEvent.value.id),
				},
				{
					id: 'events.enrollments.certificates',
					title: trans('events.certificates.title'),
					link: route('events.enrollments.certificates', currentEvent.value.id),
				},
				{
					id: 'events.enrollments.certifications.index',
					title: trans('events.certifications.title'),
					link: route('events.enrollments.certifications.index', currentEvent.value.id),
				},
				{
					id: 'events.enrollments.mailing.index',
					title: trans('events.mailing.title'),
					link: route('events.enrollments.mailing.index', {event: currentEvent.value.id, status: 'pending'}),
				},
				{
					title: trans('events.teams.title'),
					children: [
						{
							id: 'events.enrollments.teams.index',
							title: trans('events.teams.participants.title'),
							link: route('events.enrollments.teams.index', currentEvent.value.id)
						},
						{
							id: 'events.enrollments.teams.scores.index',
							title: trans('events.teams.scores.title'),
							link: route('events.enrollments.teams.scores.index', currentEvent.value.id)
						},
						{
							id: 'events.enrollments.teams.standings.index',
							title: trans('events.teams.standings.title'),
							link: route('events.enrollments.teams.standings.index', currentEvent.value.id)
						},
					]
				},
			],
		},
		{
			title: trans('events.stages.title'),
			children: [
				{
					id: 'events.stages.registration.',
					title: trans('events.stages.registration.title'),
					link: route('events.stages.registration.index', currentEvent.value.id),
				},
				{
					id: 'events.stages.selection.',
					title: trans('events.stages.selection.title'),
					link: route('events.stages.selection.index', currentEvent.value.id),
				},
				{
					id: 'events.stages.shopping.',
					title: trans('events.stages.shopping.title'),
					link: route('events.stages.shopping.index', currentEvent.value.id),
				},
				{
					id: 'events.stages.attachment.',
					title: trans('events.stages.attachment.title'),
					link: route('events.stages.attachment.index', currentEvent.value.id),
				},
				{
					id: 'events.stages.survey.',
					title: trans('events.stages.survey.title'),
					link: route('events.stages.survey.index', currentEvent.value.id),
				},
				{
					id: 'events.stages.raffle.',
					title: trans('events.stages.raffle.title'),
					link: route('events.stages.raffle.index', currentEvent.value.id),
				},
			],
		},
	],
} : null

const currentAssemblyMenuItem:MenuItem|null = currentAssembly.value ? {
	id: 'association.assemblies.',
	title: currentAssembly.value.title,
	children: [
		{
			id: 'association.assemblies.edit',
			title: trans('general.edit'),
			link: route('association.assemblies.edit', currentAssembly.value.id),
		},
		{
			id: 'association.assemblies.college-zones.representatives.',
			title: trans('assemblies.representatives.title'),
			link: route('association.assemblies.college-zones.representatives.index', currentAssembly.value.id),
		},
		{
			id: 'association.assemblies.elections.',
			title: trans('assemblies.elections.title'),
			children: [
				{
					id: 'association.assemblies.elections.college-zone-representatives.',
					title: trans('assemblies.elections.college-zone-representatives.title'),
					link: route('association.assemblies.elections.college-zone-representatives.index', currentAssembly.value.id),
				},
				{
					id: 'association.assemblies.elections.event-hosts.',
					title: trans('assemblies.elections.event-hosts.title'),
					link: route('association.assemblies.elections.event-hosts.index', currentAssembly.value.id),
				},
				{
					id: 'association.assemblies.elections.legal-ranks.',
					title: trans('assemblies.elections.legal-ranks.title'),
					link: route('association.assemblies.elections.legal-ranks.index', currentAssembly.value.id),
				},
			],
		},
	],
} : null

const currentCampaignMenuItem:MenuItem|null = currentCampaign.value ? {
	id: 'tools.campaigns.',
	title: trans('tools.campaigns.title'),
	children: [
		{
			id: 'tools.campaigns.edit',
			title: trans('tools.campaigns.edit.title'),
			link: route('tools.campaigns.edit', currentCampaign.value.id),
		},
		{
			id: 'tools.campaigns.instances.index',
			title: trans('tools.campaigns.instances'),
			link: route('tools.campaigns.instances.index', currentCampaign.value.id),
		},
		{
			id: 'tools.campaigns.send.associates.index',
			title: trans('tools.campaigns.send.associates'),
			link: route('tools.campaigns.send.associates.index', currentCampaign.value.id),
		},
		{
			id: 'tools.campaigns.send.enrollees.index',
			title: trans('tools.campaigns.send.enrollees'),
			link: route('tools.campaigns.send.enrollees.index', currentCampaign.value.id),
		},
		{
			id: 'tools.campaigns.send.students.index',
			title: trans('tools.campaigns.send.students'),
			link: route('tools.campaigns.send.students.index', currentCampaign.value.id),
		},
		{
			id: 'tools.campaigns.send.everyone.index',
			title: trans('tools.campaigns.send.everyone'),
			link: route('tools.campaigns.send.everyone.index', currentCampaign.value.id),
		},
	],
} : null

const currentBoardAreaMenuItems:Array<MenuItem> = []

currentBoard.value?.areas.forEach((area:BoardArea) => {
	currentBoardAreaMenuItems.push({
		id: 'association.boards.areas.',
		title: area.name,
		children: [
			{
				id: `association.boards.areas.full-members.index.${area.id}`,
				title: trans('association.boards.areas.members.full.title'),
				link: route('association.boards.areas.full-members.index', {board: currentBoard.value.id, boardArea: area.id}),
			},
			{
				id: `association.boards.areas.workgroup-members.index.${area.id}`,
				title: trans('association.boards.areas.members.workgroup.title'),
				link: route('association.boards.areas.workgroup-members.index', {board: currentBoard.value.id, boardArea: area.id}),
			},
		],
	})
})

const currentRotationMenuItem:MenuItem = currentRotation.value ? {
	title: currentRotation.value.title,
	children: [
		{
			id: 'association.representatives.rotations.edit',
			title: trans('rotations.title'),
			link: route('association.representatives.rotations.edit', currentRotation.value.id),
		},
		{
			id: 'association.representatives.rotations.elections.',
			title: trans('rotations.elections.title'),
			link: route('association.representatives.rotations.elections.index', currentRotation.value.id),
		},
		{
			id: 'association.representatives.rotations.voters.',
			title: trans('rotations.elections.voters.title'),
			link: route('association.representatives.rotations.voters.index', currentRotation.value.id),
		},
		{
			id: 'association.representatives.rotations.candidates.',
			title: trans('rotations.elections.candidates.title'),
			link: route('association.representatives.rotations.candidates.index', currentRotation.value.id),
		},
		{
			id: 'association.representatives.rotations.meeting-letters.',
			title: trans('rotations.elections.documentation.meeting_letters.title'),
			link: route('association.representatives.rotations.meeting-letters.index', currentRotation.value.id),
		},
		{
			id: 'association.representatives.rotations.compromise-letters.',
			title: trans('rotations.elections.documentation.compromise_letters.title'),
			link: route('association.representatives.rotations.compromise-letters.index', currentRotation.value.id),
		},
	],
} : null

const menuItems:Ref<Array<MenuItem>> = ref([
	{
		id: 'home',
		title: trans('menu.home'),
		link: route('home'),
		visible: true,
	},
	{
		title: trans('menu.events.title'),
		visible: canEditJoSEII.value || canEditCAEII.value || canEditSemEII.value || canEditLIDERA.value || canEditCiCA.value || canEditENaR.value || canEditRCD.value || canEditGeneric.value || canViewEventPeriods.value || canViewEventRequests.value,
		children: [
			{
				id: 'events.joseii.index',
				title: trans('menu.events.joseii'),
				link: route('events.joseii.index'),
				visible: canEditJoSEII.value,
			},
			{
				id: 'events.caeii.index',
				title: trans('menu.events.caeii'),
				link: route('events.caeii.index'),
				visible: canEditCAEII.value,
			},
			{
				id: 'events.semeii.index',
				title: trans('menu.events.semeii'),
				link: route('events.semeii.index'),
				visible: canEditSemEII.value,
			},
			{
				id: 'events.lidera.index',
				title: trans('menu.events.lidera'),
				link: route('events.lidera.index'),
				visible: canEditLIDERA.value,
			},
			{
				id: 'events.cica.index',
				title: trans('menu.events.cica'),
				link: route('events.cica.index'),
				visible: canEditCiCA.value,
			},
			{
				id: 'events.enar.index',
				title: trans('menu.events.enar'),
				link: route('events.enar.index'),
				visible: canEditENaR.value,
			},
			{
				id: 'events.rcd.index',
				title: trans('menu.events.rcd'),
				link: route('events.rcd.index'),
				visible: canEditRCD.value,
			},
			{
				id: 'events.generic.index',
				title: trans('menu.events.generic'),
				link: route('events.generic.index'),
				visible: canEditGeneric.value,
			},
			{
				id: 'events.periods.',
				title: trans('menu.events.periods'),
				link: route('events.periods.index'),
				visible: canViewEventPeriods.value,
			},
			{
				id: 'events.requests.',
				title: trans('menu.events.requests'),
				link: route('events.requests.index'),
				visible: canViewEventRequests.value,
			},
		],
	},
	{
		title: trans('menu.association.title'),
		visible: canViewRoles.value || canViewBoards.value || canViewBoardRepresentativeGroups.value || canViewAssemblies.value || canViewUsers.value || canViewStatistics.value,
		children: [
			{
				id: 'association.roles.',
				title: trans('menu.association.roles'),
				link: route('association.roles.index'),
				visible: canViewRoles.value,
			},
			{
				id: 'association.assemblies.',
				title: trans('menu.association.assemblies'),
				link: route('association.assemblies.index'),
				visible: canViewAssemblies.value,
			},
			{
				id: 'association.boards.',
				title: trans('menu.association.boards'),
				visible: canViewBoards.value,
				children: [
					{
						id: 'association.boards.certificates.',
						title: trans('admin.users.boards.certificates.title'),
						link: route('association.boards.certificates.index'),
					},
					{
						id: 'association.boards.certifications.',
						title: trans('admin.users.boards.certifications.title'),
						link: route('association.boards.certifications.index'),
					},
					...currentBoardAreaMenuItems,
				],
			},
			{
				id: 'association.representatives.',
				title: trans('menu.association.representatives.title'),
				visible: canViewBoardRepresentativeGroups.value || canViewRotationPeriods.value || canViewRotationRequests.value || canViewRotations.value || canViewAdmissionElections.value,
				children: [
					{
						id: 'association.representatives.certificates.',
						title: trans('menu.association.representatives.certificates'),
						link: route('association.representatives.certificates.index'),
						visible: canViewBoardRepresentativeGroups.value,
					},
					{
						id: 'association.representatives.certifications.',
						title: trans('menu.association.representatives.certifications'),
						link: route('association.representatives.certifications.index'),
						visible: canViewBoardRepresentativeGroups.value,
					},
					{
						id: 'association.representatives.periods.',
						title: trans('menu.association.representatives.periods'),
						link: route('association.representatives.periods.index'),
						visible: canViewRotationPeriods.value,
					},
					{
						id: 'association.representatives.requests.',
						title: trans('menu.association.representatives.requests'),
						link: route('association.representatives.requests.index'),
						visible: canViewRotationRequests.value,
					},
					{
						id: 'association.representatives.rotations.',
						title: trans('menu.association.representatives.rotations'),
						link: route('association.representatives.rotations.index'),
						visible: canViewRotations.value,
					},
					{
						id: 'association.representatives.admissions.',
						title: trans('menu.association.representatives.admissions'),
						link: route('association.representatives.admissions.index'),
						visible: canViewAdmissionElections.value,
					},
				],
			},
			{
				id: 'association.associates.',
				title: trans('menu.association.associates'),
				link: route('association.associates.index'),
				visible: canViewUsers.value,
			},
			{
				id: 'association.statistics.',
				title: trans('menu.association.statistics'),
				link: route('association.statistics.index'),
				visible: canViewStatistics.value,
			},
		],
	},
	{
		id: 'tools',
		title: trans('menu.tools.title'),
		visible: canViewAccreditations.value || canViewAcademicCertificates.value || canViewCampaigns.value,
		children: [
			{
				id: 'tools.accreditations.',
				title: trans('menu.tools.accreditations'),
				visible: canViewAccreditations.value,
				children: [
					{
						id: 'tools.accreditations.scan.',
						title: trans('general.qr.scan'),
						link: route('tools.accreditations.scan.index'),
						visible: canViewAccreditations.value,
					},
					{
						id: 'tools.accreditations.search.',
						title: trans('general.search'),
						link: route('tools.accreditations.search.index'),
						visible: canViewAccreditations.value,
					},
				],
			},
			{
				id: 'tools.academic-certificates.',
				title: trans('tools.academic-certificates.title'),
				visible: canViewAcademicCertificates.value,
				children: [
					{
						id: 'tools.academic-certificates.submissions.',
						title: trans('tools.academic-certificates.submissions.title'),
						link: route('tools.academic-certificates.submissions.index'),
						visible: canViewAcademicCertificates.value,
					},
					{
						id: 'tools.academic-certificates.history.',
						title: trans('tools.academic-certificates.history.title'),
						link: route('tools.academic-certificates.history.index'),
						visible: canViewAcademicCertificates.value,
					},
				],
			},
			{
				id: 'tools.campaigns.',
				title: trans('menu.tools.campaigns'),
				link: route('tools.campaigns.index'),
				visible: canViewCampaigns.value,
			},
		],
	},
	{
		id: 'settings',
		title: trans('menu.settings.title'),
		visible: canViewSettings.value || canViewCompanies.value || canViewColleges.value || canViewMajors.value || canViewLanguages.value,
		children: [
			{
				id: 'settings.system.index',
				title: trans('menu.settings.system'),
				link: route('settings.system.index'),
				visible: canViewSettings.value,
			},
			{
				id: 'settings.companies.index',
				title: trans('menu.settings.companies'),
				link: route('settings.companies.index'),
				visible: canViewCompanies.value,
			},
			{
				id: 'settings.colleges.index',
				title: trans('menu.settings.colleges'),
				link: route('settings.colleges.index'),
				visible: canViewColleges.value,
			},
			{
				id: 'settings.majors.index',
				title: trans('menu.settings.majors'),
				link: route('settings.majors.index'),
				visible: canViewMajors.value,
			},
			{
				id: 'settings.languages.index',
				title: trans('menu.settings.languages'),
				link: route('settings.languages.index'),
				visible: canViewLanguages.value,
			},
		],
	},
	{
		id: 'meetings',
		title: trans('menu.meetings.title'),
		visible: true,
		children: [
			{
				id: 'meetings.index.onboarding',
				title: trans('menu.meetings.onboarding'),
				link: route('meetings.index', {namespace: 'onboarding'}),
				visible: true,
			},
			{
				id: 'meetings.index.new-features',
				title: trans('menu.meetings.new-features'),
				link: route('meetings.index', {namespace: 'new-features'}),
				visible: true,
			},
			{
				id: 'meetings.index.review',
				title: trans('menu.meetings.review'),
				link: route('meetings.index', {namespace: 'review'}),
				visible: true,
			},
			{
				id: 'meetings.index.quick-chat',
				title: trans('menu.meetings.quick-chat'),
				link: route('meetings.index', {namespace: 'quick-chat'}),
				visible: true,
			},
		],
	},
] as Array<MenuItem>)

if(currentEventMenuItem){
	const eventTypeIndex:number = menuItems.value[1].children.findIndex((item:MenuItem) => item.id.split('.').includes(currentEvent.value.type))
	if(eventTypeIndex > -1){
		menuItems.value[1].children.splice(eventTypeIndex, 1, currentEventMenuItem)
	}
}

if(currentAssemblyMenuItem){
	menuItems.value[2].children.splice(1, 1, currentAssemblyMenuItem)
}

if(currentRotationMenuItem){
	menuItems.value[2].children[3].children.splice(4, 1, currentRotationMenuItem)
}

if(currentCampaignMenuItem){
	menuItems.value[5].children.splice(2, 1, currentCampaignMenuItem)
}

function toggleMenu():void{
	const menu:HTMLElement|null = document.getElementById('aareii-menu')
	if(menu){
		if(menu.classList.contains('flex')){
			menu.classList.add('hidden')
			menu.classList.remove('flex')
		} else {
			menu.classList.add('flex')
			menu.classList.remove('hidden')
		}
	}
}

function currentRouteNamed(...patterns:Array<string>):boolean{
	return patterns.findIndex((p:string) => currentRouteName.value.startsWith(p)) > -1
}
</script>
